import { useCallback, useEffect, useState } from 'react'

import { api, ApiError, type ChannelEntry, type ChannelSnapshot } from '../lib/api'

type Props = { onPlay: (link: string) => void }

/** Most recent of an entry's timestamps, which is what "still alive" means here. */
function lastActivity(entry: ChannelEntry): number | null {
  const times = [entry.okAt, entry.koAt, entry.addedAt]
    .map((t) => (t ? Date.parse(t) : Number.NaN))
    .filter((t) => Number.isFinite(t))
  return times.length > 0 ? Math.max(...times) : null
}

function age(at: number | null): string {
  if (at === null) return '—'
  const hours = (Date.now() - at) / 3_600_000
  if (hours < 1) return `fa ${Math.max(1, Math.round(hours * 60))} min`
  if (hours < 48) return `fa ${hours.toFixed(1)} h`
  return `fa ${Math.round(hours / 24)} dies`
}

export function ChannelsPanel({ onPlay }: Props) {
  const [slugs, setSlugs] = useState<Array<{ slug: string; name: string | null }>>([])
  const [recentHours, setRecentHours] = useState(24)
  const [selected, setSelected] = useState<string>('')
  const [snapshot, setSnapshot] = useState<ChannelSnapshot | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const { channels, recentHours: hours } = await api.getChannels()
        setSlugs(channels)
        setRecentHours(hours)
        setSelected((current) => current || channels[0]?.slug || '')
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err))
      }
    })()
  }, [])

  const load = useCallback(async (slug: string, refresh = false) => {
    if (!slug) return
    setBusy(true)
    setError(null)
    try {
      setSnapshot(await api.getChannel(slug, refresh))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [])

  useEffect(() => {
    void load(selected)
  }, [selected, load])

  const entries = snapshot?.entries ?? []

  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Canals</h2>
        <button className="btn btn--ghost" disabled={busy || !selected} onClick={() => void load(selected, true)}>
          {busy ? 'Carregant…' : 'Actualitza'}
        </button>
      </header>

      {slugs.length > 1 && (
        <div className="chanpick">
          {slugs.map((channel) => (
            <button
              key={channel.slug}
              className={`chip${channel.slug === selected ? ' chip--on' : ''}`}
              onClick={() => setSelected(channel.slug)}
            >
              {channel.name ?? channel.slug}
            </button>
          ))}
        </div>
      )}

      {error && <p className="panel__error">{error}</p>}
      {snapshot?.error && (
        <p className="panel__error">
          No s'ha pogut llegir el directori
          {snapshot.fetchedAt > 0 && `, i el que es llista és de ${age(snapshot.fetchedAt)}`}. Els
          identificadors d'aquesta antiguitat ja no solen funcionar. Motiu: {snapshot.error}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="panel__hint">{busy ? 'Carregant…' : 'Cap identificador publicat.'}</p>
      ) : (
        <ul className="cids">
          {entries.map((entry) => {
            const seen = lastActivity(entry)
            const fresh = seen !== null && Date.now() - seen < recentHours * 3_600_000
            return (
              <li key={entry.id} className={`cid${fresh ? ' cid--fresh' : ''}${snapshot?.error ? ' cid--stale' : ''}`}>
                <div className="cid__votes">
                  <span className="cid__ok" title="Vots a favor">
                    ▲ {entry.ok}
                  </span>
                  <span className="cid__ko" title="Vots en contra">
                    ▼ {entry.ko}
                  </span>
                </div>
                <div className="cid__meta">
                  <code className="cid__id">{entry.id}</code>
                  <span className="cid__age">{age(seen)}</span>
                </div>
                <button className="btn btn--primary btn--small" onClick={() => onPlay(`acestream://${entry.id}`)}>
                  Reprodueix
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {snapshot && snapshot.fetchedAt > 0 && (
        <p className="panel__hint">
          {entries.length} identificadors · llegit {age(snapshot.fetchedAt)}
        </p>
      )}
    </section>
  )
}
